import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, CheckCircle2, XCircle, Send, Eye, EyeOff, Activity,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = {
  pixel_id: null,
  name: '',
  fb_pixel_id: '',
  access_token: '',
  test_event_code: '',
  enabled: true,
};

export default function AdminPixelsPage() {
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/pixels`, { withCredentials: true });
      setPixels(r.data || []);
    } catch (e) {
      toast.error('Erro ao carregar pixels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(EMPTY);
    setEditing(false);
    setShowToken(false);
  };

  const onSave = async () => {
    if (!form.name?.trim() || !form.fb_pixel_id?.trim() || !form.access_token?.trim()) {
      toast.error('Preencha Nome, Pixel ID e Access Token');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        fb_pixel_id: form.fb_pixel_id.trim(),
        access_token: form.access_token.trim(),
        test_event_code: (form.test_event_code || '').trim() || null,
        enabled: !!form.enabled,
      };
      if (editing && form.pixel_id) {
        await axios.put(`${API}/admin/pixels/${form.pixel_id}`, payload, { withCredentials: true });
        toast.success('Pixel atualizado');
      } else {
        await axios.post(`${API}/admin/pixels`, payload, { withCredentials: true });
        toast.success('Pixel adicionado');
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (p) => {
    setForm({
      pixel_id: p.pixel_id,
      name: p.name || '',
      fb_pixel_id: p.fb_pixel_id || '',
      access_token: p.access_token || '',
      test_event_code: p.test_event_code || '',
      enabled: !!p.enabled,
    });
    setEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Remover o pixel "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/pixels/${p.pixel_id}`, { withCredentials: true });
      toast.success('Pixel removido');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao remover');
    }
  };

  const onToggle = async (p) => {
    try {
      await axios.put(`${API}/admin/pixels/${p.pixel_id}`,
        { enabled: !p.enabled }, { withCredentials: true });
      load();
    } catch (e) {
      toast.error('Erro ao alterar estado');
    }
  };

  const onTest = async (p) => {
    try {
      const r = await axios.post(`${API}/admin/pixels/${p.pixel_id}/test`, {}, { withCredentials: true });
      toast.success(r.data?.message || 'Evento de teste enviado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro no teste');
    }
  };

  const mask = (s) => {
    if (!s) return '—';
    if (s.length < 12) return '••••';
    return `${s.slice(0, 6)}…${s.slice(-4)}`;
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="admin-pixels-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="h-7 w-7 text-[#1D4ED8]" />
            Pixels Facebook
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie quantos pixels quiser. Todos disparam eventos automaticamente — no navegador e via Conversions API (server-side).
          </p>
        </div>
      </div>

      {/* Formulário */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 mb-8 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          {editing ? 'Editar pixel' : 'Adicionar novo pixel'}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wider">
              Nome interno*
            </label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex: Campanha Black Friday"
              data-testid="pixel-form-name"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wider">
              Pixel ID (Facebook)*
            </label>
            <Input
              value={form.fb_pixel_id}
              onChange={(e) => setForm({ ...form, fb_pixel_id: e.target.value.replace(/\D/g, '') })}
              placeholder="1234567890123456"
              data-testid="pixel-form-id"
              inputMode="numeric"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wider">
              Access Token (Conversions API)*
            </label>
            <div className="relative">
              <Input
                type={showToken ? 'text' : 'password'}
                value={form.access_token}
                onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                placeholder="EAA..."
                className="pr-10"
                data-testid="pixel-form-token"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                aria-label={showToken ? 'Ocultar' : 'Mostrar'}
              >
                {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 uppercase tracking-wider">
              Test Event Code <span className="text-gray-400 normal-case">(opcional, p/ validar)</span>
            </label>
            <Input
              value={form.test_event_code}
              onChange={(e) => setForm({ ...form, test_event_code: e.target.value })}
              placeholder="TEST12345"
              data-testid="pixel-form-test-code"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="h-4 w-4 accent-[#1D4ED8]"
                data-testid="pixel-form-enabled"
              />
              <span className="text-sm font-medium text-gray-700">
                Pixel ativo (disparando eventos)
              </span>
            </label>
          </div>
        </div>

        <div className="flex gap-3 mt-5">
          <Button
            onClick={onSave}
            disabled={saving}
            className="bg-[#0a0a0a] hover:bg-[#1D4ED8] text-white rounded-none px-6"
            data-testid="pixel-form-save"
          >
            {saving ? 'Salvando...' : (editing ? 'Salvar alterações' : (
              <span className="flex items-center gap-2"><Plus className="h-4 w-4" /> Adicionar pixel</span>
            ))}
          </Button>
          {editing && (
            <Button
              variant="outline"
              onClick={resetForm}
              className="rounded-none"
              data-testid="pixel-form-cancel"
            >
              Cancelar
            </Button>
          )}
        </div>

        <details className="mt-5 text-xs text-gray-600">
          <summary className="cursor-pointer text-gray-700 font-medium hover:text-gray-900">
            Como obter Pixel ID e Access Token?
          </summary>
          <ol className="list-decimal pl-5 mt-2 space-y-1 leading-relaxed">
            <li>Acesse <a className="text-[#1D4ED8] underline" href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer">business.facebook.com/events_manager</a></li>
            <li>Selecione o pixel (ou crie um novo em "Conectar fontes de dados" → "Web" → "Meta Pixel")</li>
            <li><b>Pixel ID</b>: aparece logo abaixo do nome do pixel (16 dígitos)</li>
            <li><b>Access Token</b>: aba "Configurações" → role até "Conversions API" → "Gerar token de acesso" (recomendado criar um System User)</li>
            <li><b>Test Event Code</b>: aba "Test Events" → use para validar antes de ativar em produção</li>
          </ol>
        </details>
      </div>

      {/* Lista */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Pixels cadastrados ({pixels.length})</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando…</div>
        ) : pixels.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhum pixel cadastrado ainda.<br />
            Adicione o primeiro acima para começar a rastrear conversões.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Pixel ID</TableHead>
                <TableHead>Token (CAPI)</TableHead>
                <TableHead>Test Code</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pixels.map((p) => (
                <TableRow key={p.pixel_id}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs">{p.fb_pixel_id}</TableCell>
                  <TableCell className="font-mono text-xs text-gray-500">{mask(p.access_token)}</TableCell>
                  <TableCell className="font-mono text-xs text-gray-500">{p.test_event_code || '—'}</TableCell>
                  <TableCell className="text-center">
                    <button onClick={() => onToggle(p)} title="Alternar status">
                      {p.enabled ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-200 border-green-300">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> ATIVO
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300">
                          <XCircle className="h-3 w-3 mr-1" /> INATIVO
                        </Badge>
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Enviar evento de teste para CAPI"
                        onClick={() => onTest(p)}
                        data-testid={`pixel-test-${p.pixel_id}`}
                      >
                        <Send className="h-4 w-4 text-[#1D4ED8]" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Editar"
                        onClick={() => onEdit(p)}
                        data-testid={`pixel-edit-${p.pixel_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Remover"
                        onClick={() => onDelete(p)}
                        data-testid={`pixel-delete-${p.pixel_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Eventos disparados (info) */}
      <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">
          Eventos rastreados automaticamente
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-xs text-gray-700">
          <div>• <b>PageView</b> — toda visita</div>
          <div>• <b>ViewContent</b> — abrir produto</div>
          <div>• <b>Search</b> — buscar no site</div>
          <div>• <b>AddToCart</b> — adicionar ao carrinho</div>
          <div>• <b>InitiateCheckout</b> — iniciar pagto</div>
          <div>• <b>Purchase</b> — compra concluída (server-side com CPF/email hash)</div>
          <div>• <b>CompleteRegistration</b> — novo cadastro</div>
          <div>• <b>Lead</b> — captura de e-mail</div>
        </div>
        <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
          🔒 Todos os eventos disparam <b>duas vezes</b>: navegador (Pixel) + servidor (Conversions API) com deduplicação automática via <code>event_id</code>.
          Isso garante captura de 100% das conversões mesmo com adblockers e iOS 14.5+, e match quality 9-10/10 no Meta.
        </p>
      </div>
    </div>
  );
}
